import {
    TASK_REMINDER_OFFSET_LABELS,
    TASK_REMINDER_OFFSETS,
    type TaskReminderOffset,
} from '@shared/constants/business/task';
import { z } from 'zod';

export const taskReminderOffsetSchema = z.enum(TASK_REMINDER_OFFSETS);

/**
 * Put a reminder selection into canonical form: duplicates dropped, list order restored. The
 * stored array is then independent of the order a multi-select emitted its values in, so the
 * audit history and the send job see one shape per selection.
 */
export function normaliseTaskReminderOffsets(offsets: readonly TaskReminderOffset[]): TaskReminderOffset[] {
    const selected = new Set(offsets);
    return TASK_REMINDER_OFFSETS.filter((offset) => selected.has(offset));
}

/**
 * The "Set reminder" field as the API accepts it (CUR-1276): zero or more of the fixed offsets.
 * Bounded by the list length, duplicates folded, canonical order. An empty array means no
 * reminder. The "no reminder without a due date" rule needs the due date, so it lives in the
 * task routes and `TaskService`, not here.
 */
export const taskReminderOffsetsSchema = z
    .array(taskReminderOffsetSchema)
    .max(TASK_REMINDER_OFFSETS.length)
    .transform(normaliseTaskReminderOffsets);

export type TaskReminderOffsetsInput = z.input<typeof taskReminderOffsetsSchema>;

/**
 * How many days before the due date each offset fires, for the claims task-list rule that a
 * reminder may not reach further back than the definition's days due (CUR-1279): a reminder
 * earlier than that would fire before the task is generated. "1 month before" counts as 31,
 * the longest month, so it is offered only when no calendar month can reach past the days due.
 * This is for that comparison only; the send job still resolves "1 month before" as a calendar
 * month in the org's zone.
 */
export const TASK_REMINDER_OFFSET_DAYS = {
    ON_THE_DAY: 0,
    ONE_DAY_BEFORE: 1,
    TWO_DAYS_BEFORE: 2,
    THREE_DAYS_BEFORE: 3,
    FIVE_DAYS_BEFORE: 5,
    SEVEN_DAYS_BEFORE: 7,
    TWO_WEEKS_BEFORE: 14,
    THREE_WEEKS_BEFORE: 21,
    ONE_MONTH_BEFORE: 31,
} as const satisfies Record<TaskReminderOffset, number>;

/** Whether an offset fits inside a task-list definition's days due (CUR-1279). */
export function reminderOffsetFitsDaysDue(offset: TaskReminderOffset, daysDue: number): boolean {
    return TASK_REMINDER_OFFSET_DAYS[offset] <= daysDue;
}

/**
 * Split a selection against a days-due value: the offsets that still fit, in their original
 * order, and the ones that reach past it. Shrinking the days due drops only the second group.
 */
export function partitionReminderOffsetsByDaysDue(
    offsets: readonly TaskReminderOffset[],
    daysDue: number,
): { kept: TaskReminderOffset[]; dropped: TaskReminderOffset[] } {
    const kept: TaskReminderOffset[] = [];
    const dropped: TaskReminderOffset[] = [];
    for (const offset of offsets) {
        (reminderOffsetFitsDaysDue(offset, daysDue) ? kept : dropped).push(offset);
    }
    return { kept, dropped };
}

/** "a reminder cannot be more than 2 days before the due date", shared by the form and the API. */
export function describeReminderDaysDueLimit(daysDue: number): string {
    return `a reminder cannot be more than ${daysDue} ${daysDue === 1 ? 'day' : 'days'} before the due date`;
}

/** The offsets' labels as quoted British prose: "3 days before" and "5 days before". */
export function formatReminderOffsetLabels(offsets: readonly TaskReminderOffset[]): string {
    return new Intl.ListFormat('en-GB', { type: 'conjunction' }).format(
        offsets.map((offset) => `"${TASK_REMINDER_OFFSET_LABELS[offset]}"`),
    );
}
